'use client';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  type ToggleEvent,
} from 'react';
import { cn } from '../../../utilities/classname';
import { closestFrom } from '../../../utilities/dom/closest-from';
import { hidePopover, supportsPopover } from '../../../utilities/dom/popover';

type MobileMenuContextValue = {
  /** The level on show. */
  current: string;
  push: (level: string) => void;
  back: () => void;
};

const MobileMenuContext = createContext<MobileMenuContextValue | null>(null);

const useMobileMenu = (component: string): MobileMenuContextValue => {
  const context = useContext(MobileMenuContext);
  if (context === null) { throw new Error(`${component} must be rendered inside a MobileDrawer`); }
  return context;
};

const ROOT = 'root';

const FOCUSABLE = 'a[href], button:not([disabled])';

/**
 * Hangs the sheet from the bottom of the bar it sits in, never above the
 * viewport. Outside a header surface (never the case on a site) it keeps the
 * header-height estimate.
 */
const placeSheet = (sheet: HTMLElement): void => {
  const bar = sheet.closest('[data-header-surface]');
  if (bar === null) { return; }
  sheet.style.setProperty('--nav-sheet-top', `${Math.max(0, bar.getBoundingClientRect().bottom)}px`);
};

/** One bar of the hamburger; the three fold into an X while the menu is open. */
const BAR = 'absolute left-0 h-0.5 w-5 rounded bg-current transition-all duration-200 motion-reduce:transition-none';

/**
 * The phone and tablet menu (below `xl`) of both navbars: a toggle in the
 * bar, and a sheet that drops from under the bar to the bottom of the screen.
 *
 * - The sheet is a native popover (`popover="auto"`, opened by the toggle's
 *   `popovertarget`), so the browser does the hard parts: it opens before
 *   the page has hydrated (or without JavaScript at all), it sits in the top
 *   layer above every fixed element on the page (the cookie banner, the chat
 *   bubble), a tap anywhere outside it (the logo, a bar CTA) closes it, and
 *   so does Escape. Where the Popover API is missing (Safari before 17,
 *   Chrome before 114, both inside the browserslist) the toggle's click
 *   handler opens and closes it instead, as `data-open`; the state is kept
 *   from the popover's own `beforetoggle` events either way.
 * - Its contents are capped at `max-w-xl`, so on a tablet the rows, tiles
 *   and CTAs keep a phone's proportions rather than stretching edge to edge.
 * - The sheet holds levels: the root (every nav item), and the
 *   {@link MobileLevel}s passed as `levels`, which a {@link MobileLevelButton}
 *   pushes and a {@link MobileLevelBack} pops. Pushing slides the new level in
 *   from the right, going back from the left (base.css `.nav-level-item`).
 *   Closing returns to the root. Levels are siblings, however deep they go
 *   (a services level and one level per category): a level nested inside
 *   another would be hidden with it.
 * - Every level stays in the DOM, `hidden` until shown, so the menu's links
 *   are in the served HTML.
 * - `footer` is pinned under the scrolling levels: the header's CTAs. It
 *   takes `footerClassName`, the theme the CTAs were styled against (the
 *   banner row's, the bar's), since a button themed for a dark row can be
 *   unreadable on the sheet's own background. When every CTA's view renders
 *   nothing (a link to an unpublished page), base.css hides the band
 *   (`.nav-sheet-footer`).
 * - The sheet's top is the bar's bottom, measured as it opens (into
 *   `--nav-sheet-top`; before then, and before hydration, the header-height
 *   estimate `--header-h`), so the bar (and the toggle, now an X) stays in
 *   view above it. The page stops scrolling underneath (base.css, keyed on
 *   `:popover-open` and `data-open`); without the top layer the navbar is
 *   raised above page sections that stack at z-30/z-40 (`data-nav-root`).
 * - Escape closes and returns focus to the toggle; following a link closes;
 *   focus leaving the menu closes.
 *
 * The toggle keeps the size of a default `.btn` (`btn-square`), which the
 * header-height estimate (page-header-metrics.ts) counts on.
 */
export const MobileDrawer: React.FC<{
  id: string;
  /** The root level. */
  children: ReactNode;
  /** The drill-down levels: {@link MobileLevel}s. */
  levels?: ReactNode;
  footer?: ReactNode;
  /** The pinned footer's background and text; `bg-base-100` when unset. */
  footerClassName?: string;
}> = ({ id, children, levels, footer, footerClassName }) => {
  const [open, setOpen] = useState(false);
  /** The levels pushed above the root, the current one last; empty on the root. */
  const [trail, setTrail] = useState<readonly string[]>([]);
  const [direction, setDirection] = useState<'fwd' | 'back'>('fwd');
  const sheetRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  /** The level shown before the current one, so going back can refocus the button that opened it. */
  const previous = useRef(ROOT);

  const sheetId = `mobile-menu-${id}`;
  const current = trail.at(-1) ?? ROOT;

  const reset = () => {
    setOpen(false);
    setTrail([]);
    setDirection('fwd');
  };

  const close = () => {
    const sheet = sheetRef.current;
    if (sheet !== null && supportsPopover()) { hidePopover(sheet); } else { reset(); }
  };

  /** The popover is about to open or close, by the toggle, a tap outside, Escape or `close()`. */
  const onBeforeToggle = (event: ToggleEvent<HTMLDivElement>) => {
    if (event.newState === 'open') {
      // Measured before the sheet shows, so it never paints at the estimate first.
      placeSheet(event.currentTarget);
      setOpen(true);
    } else {
      reset();
    }
  };

  /** Without the Popover API the toggle's own click opens and closes the sheet; with it, `popovertarget` does. */
  const onToggleClick = () => {
    if (supportsPopover()) { return; }
    if (open) { reset(); } else { setOpen(true); }
  };

  const menu = useMemo<MobileMenuContextValue>(() => ({
    current,
    push: (level) => {
      setDirection('fwd');
      setTrail((levels) => [...levels, level]);
    },
    back: () => {
      setDirection('back');
      setTrail((levels) => levels.slice(0, -1));
    },
  }), [current]);

  // A new level starts at the top and takes keyboard focus: its first control
  // going in, or the button that opened the level just left coming back.
  // (The button that was pressed is now hidden, so focus must go somewhere.)
  useEffect(() => {
    const left = previous.current;
    previous.current = current;
    const scroller = scrollRef.current;
    if (!open || scroller === null || left === current) { return; }

    scroller.scrollTop = 0;
    const level = scroller.querySelector<HTMLElement>(`[data-level="${current}"]`);
    const target = direction === 'back'
      ? level?.querySelector<HTMLElement>(`[data-level-target="${left}"]`)
      : level?.querySelector<HTMLElement>(FOCUSABLE);
    target?.focus();
  }, [open, current, direction]);

  // A tap before hydration opens the popover natively, with no React state
  // behind it: pick that state up, so the toggle shows an X and Escape works.
  useEffect(() => {
    const sheet = sheetRef.current;
    if (sheet === null || !supportsPopover() || !sheet.matches(':popover-open')) { return; }
    setOpen(true);
  }, []);

  useEffect(() => {
    const sheet = sheetRef.current;
    if (!open || sheet === null) { return undefined; }
    const place = () => placeSheet(sheet);
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape' || !open) { return; }
    close();
    toggleRef.current?.focus();
  };

  const onBlur = (event: FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget;
    if (next instanceof Node && !event.currentTarget.contains(next)) { close(); }
  };

  const onSheetClick = (event: MouseEvent<HTMLDivElement>) => {
    if (closestFrom(event.target, 'a[href]') !== null) { close(); }
  };

  // A pinned CTA that is a button opens something over the page: a form or
  // embed in a modal, or Calendly's popup. Before its own handler runs, focus
  // moves to the toggle, which stays in view: a modal hands focus back on
  // closing to whatever had it when it opened, and the CTA itself is about to
  // be hidden, which would drop a keyboard user at the top of the page.
  const onFooterClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    if (closestFrom(event.target, 'button') !== null) { toggleRef.current?.focus(); }
  };

  // After the CTA's handler, the sheet closes. A modal has already hidden it
  // (the browser closes open popovers for one), but Calendly's popup is a
  // plain overlay on the body, which the sheet, in the top layer, would cover.
  // A submit button is left alone: hiding its form first would stop the
  // browser checking the fields, and its form opens a modal of its own.
  const onFooterClick = (event: MouseEvent<HTMLDivElement>) => {
    const button = closestFrom(event.target, 'button');
    if (button instanceof HTMLButtonElement && button.type !== 'submit') { close(); }
  };

  return (
    <MobileMenuContext.Provider value={menu}>
      <div className="xl:hidden" onKeyDown={onKeyDown} onBlur={onBlur}>
        <button
          ref={toggleRef}
          type="button"
          className="btn btn-ghost btn-square"
          aria-label="Navigation menu"
          aria-expanded={open}
          aria-controls={sheetId}
          popoverTarget={sheetId}
          popoverTargetAction="toggle"
          onClick={onToggleClick}
        >
          <span aria-hidden="true" className="relative block h-3.5 w-5">
            <span className={cn(BAR, open ? 'top-1.5 rotate-45' : 'top-0')} />
            <span className={cn(BAR, 'top-1.5', open && 'opacity-0')} />
            <span className={cn(BAR, open ? 'top-1.5 -rotate-45' : 'top-3')} />
          </span>
        </button>

        <div
          ref={sheetRef}
          id={sheetId}
          popover="auto"
          data-mobile-menu=""
          data-open={open ? '' : undefined}
          className="nav-sheet fixed inset-x-0 bottom-0 z-50 hidden h-auto w-auto flex-col bg-base-100 text-base-content open:flex data-open:flex"
          style={{ top: 'var(--nav-sheet-top, var(--header-h, 0px))' }}
          onBeforeToggle={onBeforeToggle}
          onClick={onSheetClick}
        >
          <div
            ref={scrollRef}
            data-level-motion={direction}
            className="flex-1 overflow-x-hidden overflow-y-auto overscroll-contain px-6 py-6"
          >
            <MobileLevel id={ROOT}>{children}</MobileLevel>
            {levels}
          </div>

          {footer ? (
            <div
              className={cn('nav-sheet-footer border-t border-current/15 p-5', footerClassName ?? 'bg-base-100')}
              onClickCapture={onFooterClickCapture}
              onClick={onFooterClick}
            >
              <div className="flex max-w-xl flex-col gap-2">{footer}</div>
            </div>
          ) : null}
        </div>
      </div>
    </MobileMenuContext.Provider>
  );
};

/** A drill-down level, shown while it is the current one. */
export const MobileLevel: React.FC<{ id: string; children: ReactNode }> = ({ id, children }) => {
  const { current } = useMobileMenu('MobileLevel');

  return <div data-level={id} hidden={current !== id} className="max-w-xl">{children}</div>;
};

/** Opens a drill-down level: a full-width row with a chevron. */
export const MobileLevelButton: React.FC<{
  level: string;
  className?: string;
  children: ReactNode;
}> = ({ level, className, children }) => {
  const { push } = useMobileMenu('MobileLevelButton');

  return (
    <button
      type="button"
      data-level-target={level}
      className={cn('flex w-full min-h-11 cursor-pointer items-center justify-between gap-3 text-left', className)}
      onClick={() => push(level)}
    >
      <span>{children}</span>
      <ChevronRight aria-hidden="true" className="size-5 shrink-0 opacity-50" />
    </button>
  );
};

/** Goes back one level: "‹ Menu", or the parent level's name. */
export const MobileLevelBack: React.FC<{ className?: string; children: ReactNode }> = ({ className, children }) => {
  const { back } = useMobileMenu('MobileLevelBack');

  return (
    <button
      type="button"
      className={cn('flex w-fit min-h-11 cursor-pointer items-center gap-1 text-sm opacity-70', className)}
      onClick={back}
    >
      <ChevronLeft aria-hidden="true" className="size-4" />
      {children}
    </button>
  );
};
